import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Modal, Input, Select, Toast, Popconfirm, Form } from '@douyinfe/semi-ui';
import { IconEdit, IconDelete, IconPlus, IconSearch } from '@douyinfe/semi-icons';
import request from '../../utils/request';
import styles from './index.module.scss';
import ImageSelector from '../../components/ImageSelector';
import RichEditor from '../../components/RichEditor';
import { useSearchParams } from 'react-router-dom';
import UniImage from '../../components/UniImage';

const Articles = () => {
    const [searchParams] = useSearchParams();
    const urlTyp = searchParams.get('typ');

    const [loading, setLoading] = useState(false);
    const [dataSource, setDataSource] = useState([]);
    const [pagination, setPagination] = useState({ currentPage: 1, pageSize: 10, total: 0 });
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [currentRecord, setCurrentRecord] = useState(null);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [searchKey, setSearchKey] = useState('');
    const [searchStatus, setSearchStatus] = useState(null);
    const [searchTyp, setSearchTyp] = useState(urlTyp ? Number(urlTyp) : null);
    const [formApi, setFormApi] = useState(null);
    const [typList, setTypList] = useState([]);

    const statusMap = {
        0: { text: '正常', type: 'success' },
        1: { text: '隐藏', type: 'danger' },
    };

    useEffect(() => {
        const fetchTypList = async () => {
            try {
                const response = await request.get('/go/sp/all/list_all_article_typ/');
                if (response.errno === 0) {
                    setTypList(response.list || []);
                } else {
                    Toast.error(response.errstr || '获取分类失败');
                }
            } catch (error) {
                console.error('获取分类失败:', error);
                Toast.error('获取分类失败');
            }
        };
        fetchTypList();
    }, []);

    const fetchList = async (page = 1, limit = 10) => {
        setLoading(true);
        try {
            const response = await request.get('/go/sp/all/list_all_article/', {
                params: {
                    page: page - 1,
                    limit,
                    key: searchKey,
                    status: searchStatus === null ? -1 : searchStatus,
                    typ: searchTyp === null ? -1 : searchTyp
                }
            });
            if (response.errno === 0) {
                setDataSource(response.list || []);
                setPagination(prev => ({
                    ...prev,
                    currentPage: page,
                    total: response.total_cnt
                }));
            } else {
                Toast.error(response.errstr || '获取列表失败');
            }
        } catch (error) {
            console.error('获取列表失败:', error);
            Toast.error('获取列表失败');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (typList.length > 0) {
            fetchList();
        }
    }, [typList]);

    const handleAdd = async (values) => {
        try {
            if (urlTyp) {
                values.typ = Number(urlTyp);
            }
            const processedValues = {
                ...values,
                typ: Number(values.typ),
                content: JSON.stringify(values.content)
            };
            const response = await request.post('/go/sp/all/add_all_article/', processedValues);
            if (response.errno === 0) {
                Toast.success('添加成功');
                setShowAddModal(false);
                fetchList();
            } else {
                Toast.error(response.errstr || '添加失败');
            }
        } catch (error) {
            console.error('添加失败:', error);
            Toast.error('添加失败');
        }
    };

    const handleEdit = async (values) => {
        try {
            if (urlTyp) {
                values.typ = Number(urlTyp);
            }
            const processedValues = {
                ...values,
                typ: Number(values.typ),
                content: JSON.stringify(values.content),
                uid: currentRecord.uid
            };
            const response = await request.post('/go/sp/all/edit_all_article/', processedValues);
            if (response.errno === 0) {
                Toast.success('编辑成功');
                setShowEditModal(false);
                fetchList();
            } else {
                Toast.error(response.errstr || '编辑失败');
            }
        } catch (error) {
            console.error('编辑失败:', error);
            Toast.error('编辑失败');
        }
    };

    const handleDelete = async (uids) => {
        try {
            const response = await request.post('/go/sp/all/delete_all_article/', {
                uids: Array.isArray(uids) ? uids.join(',') : uids
            });
            if (response.errno === 0) {
                Toast.success('删除成功');
                setSelectedRowKeys([]);
                fetchList();
            } else {
                Toast.error(response.errstr || '删除失败');
            }
        } catch (error) {
            console.error('删除失败:', error);
            Toast.error('删除失败');
        }
    };

    const handleBatchEdit = async (status, uids) => {
        if (!uids || (Array.isArray(uids) && !uids.length)) {
            Toast.warning('请选择要操作的文案');
            return;
        }
        try {
            const response = await request.post('/go/sp/all/batchedit_all_article/', {
                uids: Array.isArray(uids) ? uids.join(',') : uids,
                status
            });
            if (response.errno === 0) {
                Toast.success('操作成功');
                setSelectedRowKeys([]);
                fetchList();
            } else {
                Toast.error(response.errstr || '操作失败');
            }
        } catch (error) {
            console.error('操作失败:', error);
            Toast.error('操作失败');
        }
    };

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return '-';
        const date = new Date(parseInt(timestamp));
        return date.toLocaleString();
    };

    const columns = [
        {
            title: '文案信息',
            dataIndex: 'title',
            width: 300,
            render: (text, record) => (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                    {record.image && (
                        <UniImage src={record.image} width={40} height={40} style={{ borderRadius: 4 }} alt={text} />
                    )}
                    <div style={{ fontSize: '12px' }}>
                        <div style={{ color: 'var(--semi-color-text-0)', marginBottom: '4px' }}>{text}</div>
                        <div style={{ color: 'var(--semi-color-text-2)' }}>类型: {typList.find(item => item.uid == record.typ)?.name || '-'}</div>
                        {record.content?.d && <div style={{ color: 'var(--semi-color-text-2)' }}>摘要: {record.content.d}</div>}
                    </div>
                </div>
            )
        },
        {
            title: '状态',
            dataIndex: 'status',
            width: 120,
            render: (text) => {
                const status = statusMap[text] || { text: '异常', type: 'danger' };
                return <Button theme='light' type={status.type} size='small'>{status.text}</Button>;
            }
        },
        {
            title: '排序',
            dataIndex: 'sort',
            width: 100
        },
        {
            title: '点击/点赞',
            width: 120,
            render: (_, record) => (
                <div style={{ fontSize: '12px' }}>
                    <div>点击: {record.click_cnt}</div>
                    <div>点赞: {record.like_cnt}</div>
                </div>
            )
        },
        {
            title: '创建时间/更新时间',
            dataIndex: 'update_time',
            width: 180,
            render: (text, record) => (
                <div style={{ fontSize: '12px', color: 'var(--semi-color-text-2)' }}>
                    <div>{formatTimestamp(record.create_time)}</div>
                    <div>{formatTimestamp(text)}</div>
                </div>
            )
        },
        {
            title: '操作',
            width: 200,
            render: (_, record) => (
                <Space>
                    <Button
                        type='primary'
                        icon={<IconEdit />}
                        onClick={() => {
                            setCurrentRecord(record);
                            setShowEditModal(true);
                        }}
                    >
                        编辑
                    </Button>
                    <Popconfirm
                        title="确定要删除该文案吗？"
                        onConfirm={() => handleDelete(record.uid)}
                    >
                        <Button type='danger' icon={<IconDelete />}>删除</Button>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    const renderForm = (type = 'add') => {
        const isEdit = type === 'edit';
        const initialValues = isEdit && currentRecord ? {
            ...currentRecord,
            typ: String(currentRecord.typ),
            content: typeof currentRecord.content === 'string' ? JSON.parse(currentRecord.content) : (currentRecord.content || {})
        } : {
            typ: '',
            title: '',
            image: '',
            content: {
                c: '', // 正文
                d: '', // 摘要
                s: '', // seo描述
                a: '', // 作者
                l: ''  // 链接
            },
            click_cnt: 0,
            like_cnt: 0,
            status: 0,
            sort: 0
        };

        return (
            <Form
                getFormApi={setFormApi}
                initValues={initialValues}
                onSubmit={values => {
                    if (isEdit) {
                        handleEdit(values);
                    } else {
                        handleAdd(values);
                    }
                }}
                style={{ padding: '20px' }}
            >
                <Form.Select
                    disabled={urlTyp}
                    field="typ"
                    label="文案类型"
                    style={{ width: '100%' }}
                    rules={[{ required: true, message: '请选择文案类型' }]}
                >
                    {typList.map((item) => (
                        <Select.Option key={item.uid} value={item.uid}>{item.name}</Select.Option>
                    ))}
                </Form.Select>
                <Form.Input
                    field="title"
                    label="标题"
                    rules={[{ required: true, message: '请输入标题' }]}
                />
                <Form.Slot label="封面图片">
                    <ImageSelector
                        value={formApi?.getValue('image')}
                        onChange={value => formApi?.setValue('image', value)}
                        placeholder="请上传封面图片"
                        width="100%"
                        enableTDesignIcon={true}
                        previewSize={100}
                    />
                </Form.Slot>
                <Form.Input
                    field="content.d"
                    label="摘要"
                    type="textArea"
                    autosize={{ minRows: 2, maxRows: 4 }}
                />
                <Form.Input
                    field="content.s"
                    label="SEO描述"
                    type="textArea"
                    autosize={{ minRows: 2, maxRows: 4 }}
                />
                <Form.Input
                    field="content.a"
                    label="作者"
                />
                <Form.Input
                    field="content.l"
                    label="链接"
                />
                <Form.Slot label="正文内容">
                    <RichEditor
                        value={formApi?.getValue('content.c') || ''}
                        onChange={value => formApi?.setValue('content.c', value)}
                        style={{ height: 400 }}
                    />
                </Form.Slot>
                <Form.InputNumber
                    field="sort"
                    label="排序"
                    min={0}
                    style={{ width: '100%' }}
                />
                <Form.Select
                    field="status"
                    label="状态"
                    style={{ width: '100%' }}
                >
                    {Object.entries(statusMap).map(([value, { text }]) => (
                        <Select.Option key={value} value={parseInt(value)}>{text}</Select.Option>
                    ))}
                </Form.Select>
                {urlTyp && <Form.Input field="typ" type="hidden" initValue={Number(urlTyp)} />}
                <div style={{ marginTop: 24, textAlign: 'right' }}>
                    <Space>
                        <Button type='tertiary' onClick={() => isEdit ? setShowEditModal(false) : setShowAddModal(false)}>取消</Button>
                        <Button type='primary' htmlType="submit">确定</Button>
                    </Space>
                </div>
            </Form>
        );
    };

    return (
        <div className={styles.articlesContainer}>
            <div className={styles.header}>
                <Space>
                    <Input
                        placeholder="搜索文案标题"
                        value={searchKey}
                        onChange={setSearchKey}
                        style={{ width: 200 }}
                        showClear
                    />
                    <Select
                        placeholder="文案状态"
                        value={searchStatus}
                        onChange={setSearchStatus}
                        style={{ width: 150 }}
                        showClear
                    >
                        <Select.Option value={null}>全部状态</Select.Option>
                        {Object.entries(statusMap).map(([value, { text }]) => (
                            <Select.Option key={value} value={parseInt(value)}>{text}</Select.Option>
                        ))}
                    </Select>
                    {!urlTyp && <Select
                        placeholder="文案类型"
                        value={searchTyp}
                        onChange={setSearchTyp}
                        style={{ width: 150 }}
                        showClear
                    >
                        <Select.Option value={null}>全部类型</Select.Option>
                        {typList.map((item) => (
                            <Select.Option key={item.uid} value={item.uid}>{item.name}</Select.Option>
                        ))}
                    </Select>}
                    <Button
                        type="primary"
                        onClick={() => fetchList(1, pagination.pageSize)}
                        loading={loading}
                        icon={<IconSearch />}
                    >
                        搜索
                    </Button>
                    <Button type='primary' icon={<IconPlus />} onClick={() => setShowAddModal(true)}>新增文案</Button>
                    {selectedRowKeys.length > 0 && (
                        <Space>
                            <Button type='warning' onClick={() => handleBatchEdit(1, selectedRowKeys)}>批量隐藏</Button>
                            <Button type='primary' onClick={() => handleBatchEdit(0, selectedRowKeys)}>批量显示</Button>
                            <Popconfirm
                                title={`确定要删除选中的 ${selectedRowKeys.length} 个文案吗？`}
                                onConfirm={() => handleDelete(selectedRowKeys)}
                            >
                                <Button type='danger' icon={<IconDelete />}>批量删除</Button>
                            </Popconfirm>
                        </Space>
                    )}
                </Space>
            </div>

            <Table
                loading={loading}
                columns={columns}
                dataSource={dataSource}
                pagination={{
                    currentPage: pagination.currentPage,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    onPageChange: (page) => fetchList(page, pagination.pageSize),
                    onPageSizeChange: (size) => fetchList(1, size)
                }}
                rowKey="uid"
                rowSelection={{
                    selectedRowKeys,
                    onChange: setSelectedRowKeys
                }}
            />

            <Modal
                title="新增文案"
                visible={showAddModal}
                onCancel={() => setShowAddModal(false)}
                footer={null}
                centered
                width={800}
                bodyStyle={{ maxHeight: '80vh', overflowY: 'auto' }}
            >
                {renderForm('add')}
            </Modal>

            <Modal
                title="编辑文案"
                visible={showEditModal}
                onCancel={() => setShowEditModal(false)}
                footer={null}
                centered
                width={800}
                bodyStyle={{ maxHeight: '80vh', overflowY: 'auto' }}
            >
                {renderForm('edit')}
            </Modal>
        </div>
    );
};

export default Articles;